import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DollarSign, Calendar, TrendingUp, Package } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useClientLTV } from '@/hooks/use-client-ltv';

function formatDollars(n: number | null | undefined): string {
  if (n == null) return '$0';
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}

interface Props {
  clientId: string;
}

export function ClientLTVCard({ clientId }: Props) {
  const { data, isLoading } = useClientLTV(clientId);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Profitability</CardTitle>
        </CardHeader>
        <CardContent className="h-24 bg-muted/20 rounded-md animate-pulse" />
      </Card>
    );
  }

  if (!data || data.lifetime_revenue === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <DollarSign className="w-4 h-4" /> Profitability
          </CardTitle>
          <CardDescription className="text-xs">
            No paid revenue yet from this client.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-sm flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-success" /> Profitability
            </CardTitle>
            <CardDescription className="text-xs mt-0.5">
              {data.days_as_paying_client} days as a paying client
            </CardDescription>
          </div>
          {data.monthly_run_rate > 0 && (
            <Badge variant="outline" className="gap-1 text-[11px]">
              <TrendingUp className="w-3 h-3" /> {formatDollars(data.monthly_run_rate)}/mo
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <div className="text-2xl font-bold">{formatDollars(data.lifetime_revenue)}</div>
          <div className="text-xs text-muted-foreground">Lifetime revenue</div>
        </div>

        <div className="grid grid-cols-2 gap-3 text-xs">
          <div>
            <div className="font-medium">{data.completed_sessions}</div>
            <div className="text-muted-foreground">Completed sessions</div>
          </div>
          <div>
            <div className="font-medium">{formatDollars(data.avg_revenue_per_session)}</div>
            <div className="text-muted-foreground">Avg / session</div>
          </div>
          {data.deferred_package_balance > 0 && (
            <div className="col-span-2 flex items-start gap-2 pt-1 border-t">
              <Package className="w-3.5 h-3.5 text-muted-foreground mt-0.5" />
              <div>
                <div className="font-medium">{formatDollars(data.deferred_package_balance)}</div>
                <div className="text-muted-foreground">Prepaid / unused (deferred)</div>
              </div>
            </div>
          )}
        </div>

        {data.last_session_at && (
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground border-t pt-2">
            <Calendar className="w-3 h-3" />
            Last session {formatDistanceToNow(new Date(data.last_session_at), { addSuffix: true })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
